import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, MapPin, X, Server, Hash, Calendar, Plus, Minus, Check, RotateCcw, Zap } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchInput } from '@/components/shared/SearchInput';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { LocationMiniMap } from '@/components/shared/LocationMiniMap';
import { Pagination } from '@/components/shared/Pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { useGetSessions, useSendSessionFundsCommand } from '@/services/api/endpoints/SessionService';
import type { FundsCommandType } from '@/services/api/endpoints/SessionService';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDate, formatAmount } from '@/lib/utils';
import { toast } from 'sonner';
import type { Session, SessionType } from '@/types';

const TYPE_BADGE_STYLES: Record<SessionType, string> = {
  vending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  deposit: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  withdraw: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

function SessionTypeBadge({ type, t }: { type: SessionType; t: (key: string) => string }) {
  const labels: Record<SessionType, string> = {
    vending: t('sessions.typeVending'),
    deposit: t('sessions.typeDeposit'),
    withdraw: t('sessions.typeWithdraw'),
  };
  return (
    <Badge variant="outline" className={TYPE_BADGE_STYLES[type] || ''}>
      {labels[type] || type}
    </Badge>
  );
}

function getSessionAmount(session: { type: SessionType; reservedAmount: number; totalDeposited: number; totalWithdrawn: number }) {
  switch (session.type) {
    case 'deposit':
      return { value: session.totalDeposited, color: 'text-green-600' };
    case 'withdraw':
      return { value: session.totalWithdrawn, color: 'text-amber-600' };
    default:
      return { value: session.reservedAmount, color: '' };
  }
}

export function SessionsPage() {
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const debouncedSearch = useDebounce(search);

  const hasCoords = (s: Session) => s.metadata?.userLat != null && s.metadata?.userLng != null;

  const { data, isLoading } = useGetSessions(
    page, 20, debouncedSearch, 'createdAt', 'desc',
    undefined, statusFilter || undefined, typeFilter || undefined,
    dateFrom || undefined, dateTo || undefined
  );

  const handleExportCSV = () => {
    if (!data?.items.length) return;
    const headers = ['Date', 'User', 'Machine', 'Type', 'Amount', 'Status'];
    const rows = data.items.map(s => {
      const amt = getSessionAmount(s);
      return [
        s.createdAt,
        s.user?.email || '',
        s.machine?.name || '',
        s.type || 'vending',
        amt.value,
        s.status,
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sessions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('sessions.title')}</h1>
        <Button variant="outline" onClick={handleExportCSV} disabled={!data?.items.length}>
          <Download className="w-4 h-4 mr-2" />
          CSV
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="max-w-sm flex-1">
          <SearchInput value={search} onChange={setSearch} />
        </div>
        <select
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">{t('sessions.allTypes')}</option>
          <option value="vending">{t('sessions.typeVending')}</option>
          <option value="deposit">{t('sessions.typeDeposit')}</option>
          <option value="withdraw">{t('sessions.typeWithdraw')}</option>
        </select>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">{t('sessions.allStatuses')}</option>
          <option value="pending">{t('sessions.statusPending')}</option>
          <option value="active">{t('sessions.statusActive')}</option>
          <option value="completed">{t('sessions.statusCompleted')}</option>
          <option value="expired">{t('sessions.statusExpired')}</option>
          <option value="cancelled">{t('sessions.statusCancelled')}</option>
          <option value="failed">{t('sessions.statusFailed')}</option>
        </select>
        <Input
          type="date"
          value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPage(1); }}
          className="w-40"
        />
        <Input
          type="date"
          value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPage(1); }}
          className="w-40"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('sessions.date')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('sessions.user')}</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">{t('sessions.machine')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('sessions.type')}</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">{t('sessions.amount')}</th>
                      <th className="text-center p-4 text-sm font-medium text-muted-foreground">{t('common.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((session) => {
                      const amt = getSessionAmount(session);
                      return (
                        <tr
                          key={session.id}
                          className="border-b hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => setSelectedSession(session)}
                        >
                          <td className="p-4 text-sm">{formatDate(session.createdAt)}</td>
                          <td className="p-4 text-sm">{session.user?.email || '-'}</td>
                          <td className="p-4 text-sm text-muted-foreground">{session.machine?.name || '-'}</td>
                          <td className="p-4 text-center">
                            <SessionTypeBadge type={session.type || 'vending'} t={t} />
                          </td>
                          <td className={`p-4 text-sm text-right font-medium ${amt.color}`}>
                            {amt.value > 0 ? formatAmount(amt.value) : '-'}
                          </td>
                          <td className="p-4 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <StatusBadge status={session.status} />
                              {hasCoords(session) && <MapPin className="w-3 h-3 text-primary" />}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {data?.items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-muted-foreground">
                          {t('common.noResults')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data?.items.map((session) => {
                  const amt = getSessionAmount(session);
                  return (
                    <div
                      key={session.id}
                      className="p-4 space-y-2 cursor-pointer active:bg-muted/30"
                      onClick={() => setSelectedSession(session)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">{formatDate(session.createdAt)}</span>
                        <div className="flex items-center gap-1.5">
                          <StatusBadge status={session.status} />
                          {hasCoords(session) && <MapPin className="w-3 h-3 text-primary" />}
                        </div>
                      </div>
                      <div className="text-sm">{session.user?.email || '-'}</div>
                      <div className="text-sm text-muted-foreground">{session.machine?.name || '-'}</div>
                      <div className="flex items-center justify-between">
                        <SessionTypeBadge type={session.type || 'vending'} t={t} />
                        <span className={`text-sm font-medium ${amt.color}`}>
                          {amt.value > 0 ? formatAmount(amt.value) : '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {data?.items.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    {t('common.noResults')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {data?.pagination && (
        <Pagination
          page={page}
          totalPages={data.pagination.totalPages}
          onPageChange={setPage}
        />
      )}

      {/* Session Detail Modal */}
      {selectedSession && (
        <SessionDetailModal
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          t={t}
        />
      )}
    </div>
  );
}

// ---- Session Detail Modal ----

function SessionDetailModal({ session, onClose, t }: { session: Session; onClose: () => void; t: (k: string) => string }) {
  const coords = session.metadata?.userLat != null && session.metadata?.userLng != null;
  const amt = getSessionAmount(session);
  const canSendFunds = session.status === 'active' || session.status === 'pending';
  const [fundsAmount, setFundsAmount] = useState('');
  const fundsMutation = useSendSessionFundsCommand();

  const handleFundsCommand = async (type: FundsCommandType) => {
    const needsAmount = type === 'add' || type === 'debit';
    let amount: number | undefined;
    if (needsAmount) {
      amount = Math.round(parseFloat(fundsAmount));
      if (isNaN(amount) || amount <= 0) {
        toast.error(t('sessions.invalidAmount') || 'Neispravan iznos');
        return;
      }
    }
    try {
      await fundsMutation.mutateAsync({ sessionId: session.id, type, amount, reason: 'admin' });
      toast.success(t('sessions.fundsCommandSent') || `Komanda '${type}' poslata aparatu`);
      setFundsAmount('');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Greška');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[5vh] sm:pt-[10vh]">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-xl shadow-lg border w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-background z-10 flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-lg font-semibold">{t('sessions.sessionDetails') || 'Detalji Sesije'}</h2>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-5 space-y-5">
          {/* Status + Machine */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold">{session.machine?.name || '-'}</p>
                <p className="text-xs text-muted-foreground">{session.user?.email || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SessionTypeBadge type={session.type || 'vending'} t={t} />
              <StatusBadge status={session.status} />
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">{t('sessions.amount')}</p>
              <p className={`text-lg font-bold ${amt.color}`}>
                {amt.value > 0 ? formatAmount(amt.value) : '-'}
              </p>
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">{t('sessions.date')}</p>
              <p className="text-sm font-medium">{formatDate(session.createdAt)}</p>
            </div>
          </div>

          {/* Session ID */}
          <div className="flex items-center gap-2 text-sm">
            <Hash className="w-3.5 h-3.5 text-muted-foreground" />
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono">{session.id}</code>
          </div>

          {/* Timestamps */}
          {session.completedAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              <span>Završeno: {formatDate(session.completedAt)}</span>
            </div>
          )}

          {/* Machine Funds Commands (MQTT) */}
          {canSendFunds && (
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Zap className="w-4 h-4 text-primary" />
                <span>{t('sessions.machineCommands') || 'Komande aparatu'}</span>
              </div>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={fundsAmount}
                  onChange={e => setFundsAmount(e.target.value)}
                  placeholder={t('sessions.amountRsd') || 'Iznos (RSD)'}
                  className="flex-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={fundsMutation.isPending || !fundsAmount}
                  onClick={() => handleFundsCommand('add')}
                  className="text-green-600 hover:text-green-700"
                >
                  <Plus className="w-4 h-4 mr-1" />{t('sessions.fundsAdd') || 'Dodaj'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={fundsMutation.isPending || !fundsAmount}
                  onClick={() => handleFundsCommand('debit')}
                  className="text-red-600 hover:text-red-700"
                >
                  <Minus className="w-4 h-4 mr-1" />{t('sessions.fundsDebit') || 'Oduzmi'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={fundsMutation.isPending}
                  onClick={() => handleFundsCommand('commit')}
                >
                  <Check className="w-4 h-4 mr-1" />{t('sessions.fundsCommit') || 'Potvrdi'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={fundsMutation.isPending}
                  onClick={() => handleFundsCommand('rollback')}
                >
                  <RotateCcw className="w-4 h-4 mr-1" />{t('sessions.fundsRollback') || 'Vrati'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('sessions.fundsCommandsHelp') || 'Komande se šalju direktno aparatu preko MQTT.'}
              </p>
            </div>
          )}

          {/* Map */}
          {coords && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <MapPin className="w-4 h-4 text-primary" />
                <span>Lokacija</span>
              </div>
              <LocationMiniMap
                latitude={session.metadata!.userLat!}
                longitude={session.metadata!.userLng!}
                machineLat={session.machine?.latitude}
                machineLng={session.machine?.longitude}
                geofenceRadius={session.machine?.geofenceRadius}
                height="220px"
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
                <span>Korisnik: {session.metadata!.userLat!.toFixed(6)}, {session.metadata!.userLng!.toFixed(6)}</span>
                {session.machine?.latitude && (
                  <span>Aparat: {Number(session.machine.latitude).toFixed(6)}, {Number(session.machine.longitude).toFixed(6)}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
